import React from 'react'
import { useSearchParams } from 'react-router-dom'

import { useCourseById } from '../../../../hocs/content/useCourses'
import Loading from '../../../components/layout/Loading'
import ErrorMessage from '../../../components/errors/ErrorMessage'
import PageHeading from '../../../components/layout/PageHeading'
import { OuterTabGroup } from '../../../components/form/tab-groups'
import { useI18N } from '../../../../libraries/i18n/hook'
import * as urlLib from '../../../../libraries/url'
import DocumentGrid from '../../../components/content/DocumentGrid'
import { Document } from '../../../../api/documents/types'
import VideoSections from '../../../components/content/courses/VideoSections'
import CourseCoverCard from './CoverCard'
import AuthorsCarousel from './AuthorsCarousel'
import * as coursesLib from '../../../../libraries/content/courses'
import DescriptionText from '../../../components/content/DescriptionText'

const CLASSES_TAB_ID = 'classes'
const DOCUMENTS_TAB_ID = 'documents'

type Props = { id: string }
const CourseDetails: React.FC<Props> = ({ id }) => {
  const { t } = useI18N()
  const [urlSearchParams, setUrlSearchParams] = useSearchParams()
  const { data: course, error, isLoading } = useCourseById(id)
  if(isLoading) return <Loading size='fill-container' />
  if(error) return <ErrorMessage error={error} />
  if(!course) return null

  const handleUrlParamChange = (obj: Record<string, string | string[]>) => {
    // if obj is an empty object, remove all params
    if(Object.keys(obj).length === 0) {
      setUrlSearchParams({ tab: activeTab }, { replace: true })
    } else {
      setUrlSearchParams(urlLib.buildUrlParams(obj, urlSearchParams), { replace: true })
    }
  }

  const renderClassesExplorer = () => {
    return <VideoSections key={`${course.title}-sections`} courseId={id} sections={course.sections} />
  }

  const renderDocumentsExplorer = () => {
    return <DocumentGrid mdCols={3} lgCols={4} data={course.documents as Document[]} isLoading={isLoading} error={error} />
  }

  const activeTab = urlSearchParams.get('tab') || CLASSES_TAB_ID
  const videosAmount = course.sections?.reduce((acc, section) => acc + (section?.videos?.length ?? 0), 0) || 0
  const documentsAmount = course.documents?.length || 0
  const nextVideoId = coursesLib.getNextVideoId(course)
  return (
    <>
      <PageHeading>{course.title}</PageHeading>
      <div className='md:flex gap-10'>
        <div className='flex-1'>
          <div className='my-4'>
            <DescriptionText>{course.description}</DescriptionText>
          </div>
          <OuterTabGroup
            tabs={[
              { id: CLASSES_TAB_ID, title: `${t('courses.class', { context: 'plural' })} (${videosAmount})`, disabled: videosAmount === 0 },
              { id: DOCUMENTS_TAB_ID, title: `${t('courses.supportMaterial')} (${documentsAmount})`, disabled: documentsAmount === 0 }
            ]}
            handleClick={(tabId: string) => {
              const newParams:Record<string, string> = { tab: tabId }
              handleUrlParamChange(newParams)
            }}
            active={activeTab}
          />
          {activeTab === CLASSES_TAB_ID && renderClassesExplorer()}
          {activeTab === DOCUMENTS_TAB_ID && renderDocumentsExplorer()}
        </div>
        <div className='inline-flex flex-col gap-y-6 w-full px-6 mt-6 md:px-0 md:mt-0 md:w-[30%] md:max-w-[400px]'>
          <CourseCoverCard course={course} nextVideoId={nextVideoId} />
          <AuthorsCarousel authors={course.authors}/>
        </div>
      </div>
    </>
  )
}

export default CourseDetails
